import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { DestroyRef, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { RestaurantTableService, WatchdogService } from './lib/services';
import {
  CORE_APP_VERSION,
  CORE_DEBUG,
  CORE_DEVICE_LOCALE,
  CORE_DEVICE_NUMBERING_SYSTEM,
  CORE_DEVICE_TIMEZONE,
  CORE_STARTUP_DATE,
  CORE_STARTUP_ID,
  CORE_STARTUP_MODE,
} from './core.tokens';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const initSentry = (dsn: string, environment: string) => {
  Sentry.init({
    dsn,
    environment,
  });
};

export const provideCoreSentryErrorHandler = () => (
  [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        extractor(error: unknown, defaultExtractor: (error: unknown) => unknown): unknown {
          console.error('Sentry Error Handler:', error);
          return defaultExtractor(error);
        },
      }),
    },
    provideAppInitializer(() => {
      const destroyRef = inject(DestroyRef);
      const logger = inject(WatchdogService).tag('App', 'red');
      const debug = inject(CORE_DEBUG);
      const startupId = inject(CORE_STARTUP_ID);
      const startupMode = inject(CORE_STARTUP_MODE);
      const startupDate = inject(CORE_STARTUP_DATE);
      const appVersion = inject(CORE_APP_VERSION);
      const deviceTimezone = inject(CORE_DEVICE_TIMEZONE);
      const deviceLocale = inject(CORE_DEVICE_LOCALE);
      const deviceNumberingSystem = inject(CORE_DEVICE_NUMBERING_SYSTEM);
      const restaurantTable = inject(RestaurantTableService);

      logger.log('Sentry App Initializer');

      Sentry.setTag('debug', debug ? 'yes' : 'no');
      Sentry.setTag('version', appVersion);

      Sentry.setTag('startup.id', startupId);
      Sentry.setTag('startup.mode', startupMode);
      Sentry.setTag('startup.date',
        startupDate.getUTCDate().toString()
        + '/'
        + startupDate.getUTCMonth().toString()
        + '/'
        + startupDate.getUTCFullYear().toString(),
      );

      Sentry.setTag('device.timezone', deviceTimezone);
      Sentry.setTag('device.locale', deviceLocale);
      Sentry.setTag('device.numbering_system', deviceNumberingSystem);

      Sentry.setTag('restaurant', 'unknown');
      Sentry.setTag('restaurant.id', 'unknown');
      Sentry.setTag('restaurant.table', 'unknown');
      Sentry.setTag('restaurant.table.id', 'unknown');

      restaurantTable.table$.pipe(
        takeUntilDestroyed(destroyRef),
      ).subscribe((table) => {
        Sentry.setTag('restaurant', table ? table.restaurantName : 'unknown');
        Sentry.setTag('restaurant.id', table ? table.restaurantId : 'unknown');
        Sentry.setTag('restaurant.table', table ? table.tableName : 'unknown');
        Sentry.setTag('restaurant.table.id', table ? table.tableId : 'unknown');
      });
    }),
  ]
);

export const provideCoreSentryTrace = () => (
  [
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      const logger = inject(WatchdogService).tag('App', 'red');

      inject(Sentry.TraceService);

      logger.log('Trace Initialized');
    }),
  ]
);
